import type { GridColDef } from '@mui/x-data-grid';
import type { BillingCycleInvoice } from '@weft/api-definition';
import { isNotNullOrUndefined } from '@weft/utils';
import { VyTableCell } from 'apps/app/src/components/new-design/table/vy-table/VyTableCell';
import { compareDatesDescending, formatDate } from 'apps/app/src/utils/date';
import type { FieldPath } from 'react-hook-form';
import { Price } from '../../../components/design/Price';
import { PercentageChange } from './PercentageChange';

const BILLING_CYCLE_COLUMNS_KEYS = Object.freeze({
  EXTERNAL_ID: 'customerExternalId',
  CUSTOMER_NAME: 'customerName',
  INVOICE_NUMBER: 'id',
  INVOICE_DATE: 'billingCycle.startTime',
  AMOUNT: 'revenueBreakdown.total',
  ERP_ID: 'erpDisplayId',
  PREVIOUS_INVOICE_AMOUNT: 'previousInvoiceAmount',
  CONTRACT_NAME: 'contractName',
} satisfies Record<string, FieldPath<BillingCycleInvoice>>);

export const BILLING_CYCLE_COLUMNS: GridColDef<BillingCycleInvoice>[] = [
  {
    field: BILLING_CYCLE_COLUMNS_KEYS.EXTERNAL_ID,
    headerName: 'ID',
    flex: 1,
    minWidth: 60,
    valueGetter: ({ row }) => row.customerExternalId ?? '--',
    renderCell: ({ row }) => <VyTableCell value={row.customerExternalId ?? '--'}/>,
  },
  {
    field: BILLING_CYCLE_COLUMNS_KEYS.CUSTOMER_NAME,
    headerName: 'Customer',
    headerAlign: 'center',
    align: 'center',
    minWidth: 150,
    flex: 1,
    renderCell: ({ row }) => <VyTableCell value={row.customerName ?? '--'}/>,
  },
  {
    field: BILLING_CYCLE_COLUMNS_KEYS.INVOICE_NUMBER,
    headerName: 'Invoice',
    headerAlign: 'center',
    align: 'center',
    minWidth: 90,
    flex: 1,
    valueGetter: ({ row }) => row.id?.slice(-5) ?? '--',
  },
  {
    field: BILLING_CYCLE_COLUMNS_KEYS.INVOICE_DATE,
    headerName: 'Date',
    headerAlign: 'center',
    align: 'center',
    minWidth: 100,
    flex: 1,
    valueGetter: ({ row }) => formatDate(row.billingCycle.startTime),
    sortComparator: (invoiceStartTime1, invoiceStartTime2) => compareDatesDescending(invoiceStartTime1, invoiceStartTime2),
  },
  {
    field: BILLING_CYCLE_COLUMNS_KEYS.AMOUNT,
    headerName: 'Amount',
    headerAlign: 'center',
    align: 'center',
    minWidth: 150,
    flex: 1,
    renderCell: ({ row }) => <Price currency={row.currency}>{row.revenueBreakdown.total}</Price>,
  },
  {
    field: BILLING_CYCLE_COLUMNS_KEYS.ERP_ID,
    headerName: 'ERP ID',
    headerAlign: 'center',
    align: 'center',
    minWidth: 90,
    flex: 1,
    valueGetter: ({ row }) => row.erpDisplayId ?? '--',
  },
  {
    field: 'amountChangePercentage',
    headerName: 'Amount Change (%)',
    headerAlign: 'center',
    align: 'center',
    minWidth: 170,
    flex: 1,
    renderCell: ({ row }) => (
      <PercentageChange
        oldValue={row.previousInvoiceAmount ?? 0}
        newValue={row.revenueBreakdown.total}
      />
    ),
  },
  {
    field: BILLING_CYCLE_COLUMNS_KEYS.PREVIOUS_INVOICE_AMOUNT,
    headerName: 'Prev. Invoice',
    headerAlign: 'center',
    align: 'center',
    minWidth: 110,
    flex: 1,
    renderCell: ({ row }) => isNotNullOrUndefined(row.previousInvoiceAmount) ? <Price currency={row.currency}>{row.previousInvoiceAmount}</Price> : '--',
  },
  {
    field: BILLING_CYCLE_COLUMNS_KEYS.CONTRACT_NAME,
    headerName: 'Contract',
    headerAlign: 'center',
    align: 'center',
    minWidth: 110,
    flex: 1,
    renderCell: ({ row }) => <VyTableCell value={row.contractName ?? '--'}/>,
  },
];